import { useState } from 'react';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import apiClient from '@/services/api-client';

interface DiagnosticResult {
  timestamp: string;
  externalApiBaseConfigured: boolean;
  externalApiBase: string;
  tests: Array<{
    name: string;
    success: boolean;
    status?: number;
    response?: any;
    error?: string;
    note?: string;
  }>;
}

export function ComprehensiveDiagnostic() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DiagnosticResult | null>(null);
  const [loginTest, setLoginTest] = useState<any>(null);
  const [testEmail, setTestEmail] = useState('');
  const [testPassword, setTestPassword] = useState('');

  const runDiagnostic = async () => {
    setLoading(true);
    setResult(null);
    setLoginTest(null);

    try {
      // Run AWS diagnostic using apiClient (automatic logging + token handling)
      const diagData = await apiClient.get<DiagnosticResult>('/diagnostic');
      setResult(diagData);

      // Run login test only if credentials provided
      if (testEmail && testPassword) {
        const loginData = await apiClient.post<any>('/auth/login', {
          email: testEmail,
          password: testPassword,
        });
        setLoginTest(loginData);
      }
    } catch (error: any) {
      logger.error('Diagnostic error', error as Error, undefined, 'ComprehensiveDiagnostic');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (success: boolean, status?: number) => {
    if (success) {
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    }
    if (status === 401 || status === 400) {
      return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    }
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#FDB022] mb-2">
              🔍 Comprehensive AWS Cognito Diagnostic
            </h1>
            <p className="text-white/70">
              Complete diagnostic suite for debugging the 500 error
            </p>
          </div>
          <Button
            onClick={runDiagnostic}
            disabled={loading}
            className="bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-semibold"
            data-testid="comprehensive-diagnostic-run-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running Tests...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Run Full Diagnostic
              </>
            )}
          </Button>
        </div>

        {/* Test Credentials Input */}
        <Card className="border-2 border-white/10 bg-white/5">
          <CardHeader>
            <CardTitle>Login Test Credentials (Optional)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-white/70 mb-2 block">Test Email</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter test email"
                className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded text-white"
              />
            </div>
            <div>
              <label className="text-sm text-white/70 mb-2 block">Test Password</label>
              <input
                type="password"
                value={testPassword}
                onChange={(e) => setTestPassword(e.target.value)}
                placeholder="Enter test password"
                className="w-full px-3 py-2 bg-black/30 border border-white/20 rounded text-white"
              />
            </div>
            <p className="text-xs text-white/50">
              Leave empty to skip login test. Credentials are not stored.
            </p>
          </CardContent>
        </Card>

        {/* Configuration Status */}
        {result && (
          <Card className="border-2 border-white/10 bg-white/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {result.externalApiBaseConfigured ? (
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
                Configuration Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-white/70">External API Configured:</span>
                <span
                  className={`font-semibold ${
                    result.externalApiBaseConfigured ? 'text-green-400' : 'text-red-400'
                  }`}
                >
                  {result.externalApiBaseConfigured ? 'YES' : 'NO'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/70">API Base URL:</span>
                <code className="text-xs bg-white/10 px-2 py-1 rounded">
                  {result.externalApiBase}
                </code>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/70">Test Run At:</span>
                <span className="text-xs text-white/60">
                  {new Date(result.timestamp).toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Test Results */}
        {result && result.tests.length > 0 && (
          <Card className="border-2 border-white/10 bg-white/5">
            <CardHeader>
              <CardTitle>API Endpoint Tests</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {result.tests.map((test, index) => (
                <div
                  key={index}
                  className="border border-white/10 rounded-lg p-4 bg-black/20"
                >
                  <div className="flex items-start gap-3 mb-3">
                    {getStatusIcon(test.success, test.status)}
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-1">{test.name}</h3>
                      {test.note && (
                        <p className="text-xs text-blue-400 mb-2">{test.note}</p>
                      )}
                      <div className="flex gap-4 text-sm">
                        {test.status && (
                          <span
                            className={`font-mono ${
                              test.success
                                ? 'text-green-400'
                                : test.status >= 400 && test.status < 500
                                ? 'text-yellow-400'
                                : 'text-red-400'
                            }`}
                          >
                            HTTP {test.status}
                          </span>
                        )}
                        {test.error && (
                          <span className="text-red-400 text-xs">{test.error}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {test.response && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm text-white/70 hover:text-white">
                        View Response
                      </summary>
                      <pre className="mt-2 bg-black/40 border border-white/10 rounded p-3 overflow-x-auto text-xs">
                        {JSON.stringify(test.response, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Login Test Results */}
        {loginTest && (
          <Card
            className={`border-2 ${
              loginTest.success
                ? 'border-green-500/30 bg-green-500/5'
                : 'border-red-500/30 bg-red-500/5'
            }`}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {loginTest.success ? (
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
                Login Test with Real Credentials
                <span className="ml-auto text-sm font-mono">
                  HTTP {loginTest.status}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-black/20 p-3 rounded">
                <div className="text-sm text-white/70 mb-1">Test Account:</div>
                <code className="text-xs">agents@somethingproperty.co.za</code>
              </div>

              {loginTest.response && (
                <div>
                  <div className="text-sm text-white/70 mb-2">AWS Response:</div>
                  <pre className="bg-black/40 border border-white/10 rounded p-3 overflow-x-auto text-xs">
                    {JSON.stringify(loginTest.response, null, 2)}
                  </pre>
                </div>
              )}

              {loginTest.error && (
                <div className="bg-red-900/20 border border-red-500/30 rounded p-3">
                  <div className="text-sm font-semibold text-red-400 mb-2">Error:</div>
                  <pre className="text-xs text-red-200">
                    {JSON.stringify(loginTest.error, null, 2)}
                  </pre>
                </div>
              )}

              {!loginTest.success && loginTest.status === 500 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-4">
                  <div className="flex gap-2 items-start">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 text-sm">
                      <p className="font-semibold text-amber-400">
                        500 Internal Server Error Detected
                      </p>
                      <p className="text-white/80">
                        The AWS Lambda function is crashing. This is NOT a network issue
                        or authentication failure. Check:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-white/70 ml-2">
                        <li>AWS CloudWatch logs for the exact error</li>
                        <li>Phone number formatting code in the login handler</li>
                        <li>Try-catch blocks around the login logic</li>
                        <li>Environment variables (USER_POOL_ID, CLIENT_ID)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* No results yet */}
        {!loading && !result && (
          <Card className="border-2 border-blue-500/30 bg-blue-500/5">
            <CardContent className="py-12 text-center">
              <RefreshCw className="w-16 h-16 text-blue-400 mx-auto mb-4 opacity-50" />
              <p className="text-white/70">
                Click "Run Full Diagnostic" to test all AWS API endpoints
              </p>
            </CardContent>
          </Card>
        )}

        {/* Quick Links */}
        <Card className="border-2 border-white/10 bg-white/5">
          <CardHeader>
            <CardTitle>Quick Links & Resources</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <a
              href="https://console.aws.amazon.com/cloudwatch/home?region=us-east-1#logsV2:log-groups"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-400 hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              AWS CloudWatch Logs (us-east-1)
            </a>
            <a
              href="https://console.aws.amazon.com/lambda/home?region=us-east-1#/functions"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-400 hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              AWS Lambda Functions (us-east-1)
            </a>
            <a
              href="https://console.aws.amazon.com/cognito/home?region=us-east-1"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-400 hover:underline"
            >
              <ExternalLink className="w-4 h-4" />
              AWS Cognito User Pools (us-east-1)
            </a>
            <div className="pt-3 border-t border-white/10">
              <p className="text-sm text-white/70">
                📖 See <code className="bg-white/10 px-1 rounded">/AWS_LAMBDA_DEBUG_GUIDE.md</code> for detailed troubleshooting steps
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
